/** @jsxImportSource @emotion/react */
import { css, useTheme } from '@emotion/react';
import { Link } from '@reach/router';
import { DASHBOARD_MENU_PAGE_SUB_PAGE_PLACEHOLDER } from 'App';
import { TextCurrencyIcon } from 'components/currencyIcon/CurrencyIcon';
import { formatPriceWithLocale } from 'helpers/formatPriceWithCommas';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from 'reducers/componentResources';
import {
    CardAccountLink,
    CryptoAccountLink,
    ElementType,
    FiatAccountLink,
    MergedAccountLink,
    SideMenuLink,
} from './SideMenu';

//icons
import { useBasePath } from 'helpers/useBasePath';
import { AssetCodeFormatter } from 'components/AssetCodeFormatter';
import { CardIcon } from 'assets/CardIcon';

type AccountListProps = {
    link: SideMenuLink;
    isLinkActive: (link: string) => boolean;
    cultureCode: string | null;
    icons?: Icon[] | null;
    elementType?: ElementType;
    stablecoinAccount?: CryptoAccountLink | null;
};
export const AccountList: React.FC<AccountListProps> = ({
    link,
    isLinkActive,
    cultureCode,
    icons,
    elementType,
    stablecoinAccount,
}) => {
    const [accountsExpanded, setAccountsExpanded] = useState(false);
    const basePath = useBasePath();
    const theme = useTheme();
    const { t, i18n } = useTranslation('sidebar');

    const getRoutingLabel = (routingNumber: string) => {
        const key = `accountLabel_${routingNumber}`;
        return i18n.exists(`sidebar:${key}`) ? t(key) : routingNumber;
    };

    const translateAccountName = (accountName: React.ReactNode) => {
        if (typeof accountName !== 'string') return accountName;
        // Translate full account names like "US Checking Account" → "Cuenta Corriente"
        const fullKey = `accountName_${accountName.replace(/\s+/g, '_')}`;
        if (i18n.exists(`sidebar:${fullKey}`)) {
            return t(fullKey);
        }
        // Translate known prefixes like "DDA - 123456" → "Cuenta Corriente - 123456"
        const prefixMatch = accountName.match(/^(\w+)(\s*-\s*.+)$/);
        if (prefixMatch) {
            const [, prefix, rest] = prefixMatch;
            const key = `accountLabel_${prefix}`;
            if (i18n.exists(`sidebar:${key}`)) {
                return `${t(key)}${rest}`;
            }
        }
        
        // Generic fallback: translate patterns like "EUR Account", "GBP Account", etc.
        const currencyAccountMatch = accountName.match(/^([A-Z]{3})\s+Account\b/);
        if (currencyAccountMatch) {
            const [, code] = currencyAccountMatch;
            if (i18n.exists('sidebar:cryptoAccountName')) {
                return t('cryptoAccountName', { code });
            }
        }

        return accountName;
    };

    const isPrimaryCheckingAccount = (subLink: MergedAccountLink): boolean => {
        if (!isFiatLink(subLink) && !isAccountLink(subLink)) return false;
        const name = typeof subLink.accountName === 'string' ? subLink.accountName : '';
        const normalized = name.toLowerCase().trim();
        const isUSD = (subLink as FiatAccountLink).currencyCode === 'USD';
        return (
            normalized === 'us checking account' ||
            normalized.includes('us checking account') ||
            (normalized.includes('checking') && isUSD) ||
            (isUSD &&
                (normalized.startsWith('dda') ||
                    (subLink as FiatAccountLink).routingNumber === 'DDA'))
        );
    };

    const isHighlightedStablecoin = (subLink: MergedAccountLink): boolean => {
        if (!isCryptoLink(subLink)) return false;
        const code = (subLink as CryptoAccountLink).code?.toUpperCase?.() ?? '';
        return code === 'DUSD' || code === 'USDC';
    };

    const isWeb3BankAccount = (subLink: MergedAccountLink): boolean => {
        if (!isFiatLink(subLink) && !isAccountLink(subLink)) return false;
        const name = typeof subLink.accountName === 'string' ? subLink.accountName : '';
        return name.toLowerCase().includes('web3');
    };

    /** Display order for funding section: 0 = US Checking, 1 = Web3 Bank, 2 = other */
    const getAccountDisplayOrder = (subLink: MergedAccountLink): number => {
        if (isPrimaryCheckingAccount(subLink)) return 0;
        if (isWeb3BankAccount(subLink)) return 1;
        return 2;
    };

    const orderedAccounts = useMemo(() => {
        if (!link.accounts?.length) return link.accounts ?? [];
        return [...link.accounts].sort(
            (a, b) => getAccountDisplayOrder(a) - getAccountDisplayOrder(b)
        );
    }, [link.accounts]);

    /** Check if account has a personal/named account (not a standard system name) */
    const isPersonalNamedAccount = (subLink: MergedAccountLink): boolean => {
        if (!isFiatLink(subLink) && !isAccountLink(subLink)) return false;
        const name = typeof subLink.accountName === 'string' ? subLink.accountName : '';
        const normalized = name.toLowerCase().trim();
        // Exclude standard system account names
        if (
            normalized.includes('checking') ||
            normalized.includes('web3') ||
            normalized.startsWith('dda') ||
            normalized.includes('savings') ||
            normalized === ''
        ) {
            return false;
        }
        // If it doesn't match standard patterns, it's likely a personal named account
        return true;
    };

    const accountLinksCss = css`
        .ExpandButton {
            color: ${theme.colors.first};
        }
        .ExpandButton.Expanded {
            color: ${theme.colors.second};
        }
        .SideMenuLink .AccountValue,
        .SideMenuLink .AccountNumber,
        .SideMenuLink .CurrencyBalance {
            color: ${theme.colors.first};
        }
        .SideMenuLink.Active .AccountValue,
        .SideMenuLink.Active .AccountNumber,
        .SideMenuLink.Active .CurrencyBalance {
            color: ${theme.colors.second};
        }
        .SideMenuLink .NameOnCard {
            color: ${theme.colors.first};
        }
        .SideMenuLink.Active .NameOnCard,
        .SideMenuLink.Active .AccountName {
            color: ${theme.colors.second};
        }
        .SideMenuLink.PrimaryAccount .AccountName {
            color: ${theme.colors.first};
            font-size: 1em;
            font-weight: 700;
            letter-spacing: 0.02em;
        }
        .SideMenuLink.PrimaryAccount.Active .AccountName {
            color: ${theme.colors.second};
        }
        .SideMenuLink.HighlightedStablecoin .AccountName {
            color: ${theme.colors.first};
            font-size: 1em;
            font-weight: 700;
            letter-spacing: 0.02em;
        }
        .SideMenuLink.HighlightedStablecoin.Active .AccountName {
            color: ${theme.colors.second};
        }
    `;

    const mapFn = (subLink: MergedAccountLink, index: number) => {
        const linkHasChildren = !!subLink.childElements;
        const finalUrlSegment = `${
            linkHasChildren
                ? `${subLink.childElements[0].path}`
                : DASHBOARD_MENU_PAGE_SUB_PAGE_PLACEHOLDER
        }`;
        const isActive = isLinkActive(basePath + `/${finalUrlSegment}`);
        const isPersonal = isPersonalNamedAccount(subLink);
        const isWeb3 = isWeb3BankAccount(subLink);
        const linkClass = `SideMenuLink ${isActive ? 'Active' : ''} ${isPersonal || isWeb3 ? 'PersonalAccount' : ''}`;

        if (isAccountLink(subLink)) {
            const getAccountDisplayName = () => {
                const name = translateAccountName(subLink.accountName) ?? '';
                if (subLink.accountName === 'US Checking Account' && subLink.accountNumber) {
                    return `${name} - ${subLink.accountNumber.slice(-4)}`;
                }
                return name;
            };
            const displayBalance =
                isWeb3 && stablecoinAccount
                    ? subLink.balance + stablecoinAccount.balance
                    : subLink.balance;
            return (
                <Link
                    className={`${linkClass} notranslate`}
                    translate="no"
                    to={`${basePath}/${finalUrlSegment}`}
                    key={index}
                >
                    <TextCurrencyIcon currencySymbol={subLink.currencySymbol} active={isActive} />
                    <div className="AccountDetails">
                        <h5 className="AccountName">{getAccountDisplayName()}</h5>
                        <h5 className="CurrencyBalance">{`${
                            subLink.currencySymbol ?? ''
                        } ${formatPriceWithLocale(displayBalance, cultureCode ?? 'en-GB')}`}</h5>
                    </div>
                </Link>
            );
        }

        if (isCardLink(subLink)) {
            const getCardTypeLabel = () => {
                if (!subLink.cardType) return '';
                const key = `cardType_${subLink.cardType}`;
                return i18n.exists(`sidebar:${key}`) ? t(key) : `${subLink.cardType} card`;
            };
            const getDdaLabel = () => {
                if (!subLink.accountNumber) return null;
                return `${getRoutingLabel('DDA')}: ${subLink.accountNumber.slice(-4)}`;
            };
            const getCrbLabel = () => {
                if (!subLink.accountNumber) return null;
                return `US Checking Account: ${subLink.accountNumber.slice(-4)}`;
            };
            const isGirasol = subLink.productDisplayName === 'Girasol';
            const isCrossRiverBank = subLink.productDisplayName === 'CrossRiverBank';
            return (
                <Link
                    className={`${linkClass} notranslate`}
                    translate="no"
                    to={`${basePath}/${finalUrlSegment}`}
                    key={index}
                >
                    <CardIcon />
                    <div className="AccountDetails">
                        {isGirasol ? (
                            <>
                                <h5 className="NameOnCard">{subLink.nameOnCard}</h5>
                                <h5 className="CardTypeLabel">{getCardTypeLabel()}</h5>
                                <p className="CardNumber">{subLink.cardNumber}</p>
                            </>
                        ) : isCrossRiverBank ? (
                            <>
                                <h5 className="NameOnCard">{subLink.nameOnCard}</h5>
                                <h5 className="CardTypeLabel">{getCardTypeLabel()}</h5>
                                <p className="CardNumber">{subLink.cardNumber}</p>
                                {getCrbLabel() && (
                                    <h5 className="AccountNumber">{getCrbLabel()}</h5>
                                )}
                            </>
                        ) : (
                            <>
                                <h5 className="AccountName">{subLink.accountName}</h5>
                                <h5 className="NameOnCard">{subLink.nameOnCard}</h5>
                                <p className="CardNumber">{subLink.cardNumber}</p>
                                {getDdaLabel() && (
                                    <h5 className="AccountNumber">{getDdaLabel()}</h5>
                                )}
                            </>
                        )}
                    </div>
                </Link>
            );
        }

        if (elementType === 'Cards' && !isCardLink(subLink)) {
            //pending cards don't have card numbers
            return (
                <div
                    className={`Link ${linkClass} Card CardPending notranslate`}
                    translate="no"
                    key={index}
                >
                    <CardIcon />
                    <div className="AccountDetails">
                        <h5 className="AccountName">{t('newTopUpCardPending')}</h5>
                    </div>
                </div>
            );
        }

        if (isFiatLink(subLink)) {
            const displayBalance =
                isWeb3 && stablecoinAccount
                    ? subLink.balance + stablecoinAccount.balance
                    : subLink.balance;
            return (
                <Link
                    className={`${linkClass} notranslate`}
                    translate="no"
                    to={`${basePath}/${finalUrlSegment}`}
                    key={index}
                >
                    <TextCurrencyIcon currencySymbol={subLink.currencySymbol} active={isActive} />
                    <div className="AccountDetails">
                        <h5 className="AccountName">
                            {translateAccountName(subLink.accountName) ?? ''}
                        </h5>
                        {isWeb3 ? (
                            <h5 className="CurrencyBalance">{`${
                                subLink.currencySymbol ?? ''
                            } ${formatPriceWithLocale(displayBalance, cultureCode ?? 'en-GB')}`}</h5>
                        ) : (
                            <div className="AccountNumber">{`${
                                subLink.routingNumber ? getRoutingLabel(subLink.routingNumber) : ''
                            }${
                                subLink.routingNumber && subLink.accountNumber ? ' - ' : ''
                            }${subLink.accountNumber ?? ''}`}</div>
                        )}
                    </div>
                </Link>
            );
        }
        const typedLink = parseAsFiatAccountLink(subLink, t);

        return (
            <Link
                className={`${linkClass} notranslate`}
                translate="no"
                to={`${basePath}/${finalUrlSegment}`}
                key={index}
            >
                <TextCurrencyIcon currencySymbol={typedLink.currencySymbol} active={isActive} />
                <div className="AccountDetails">
                    <h5 className="AccountName">{typedLink.accountName ?? ''}</h5>
                    <h5 className="CurrencyBalance">{`${
                        typedLink.currencySymbol ?? ''
                    } ${formatPriceWithLocale(subLink.balance, cultureCode ?? 'en-GB')}`}</h5>
                    <div className="AccountNumber">{`${typedLink.routingNumber ? getRoutingLabel(typedLink.routingNumber) : ''}${
                        typedLink.routingNumber && typedLink.accountNumber ? ' - ' : ''
                    }${typedLink.accountNumber ?? ''}`}</div>
                </div>
            </Link>
        );
    };

    return (
        <>
            {orderedAccounts && orderedAccounts.length > 4 ? (
                <div css={accountLinksCss}>
                    {orderedAccounts.slice(0, accountsExpanded ? undefined : 3).map(mapFn)}
                    <div
                        className={`SideMenuLink ExpandButton ${
                            accountsExpanded ? 'Expanded' : ''
                        }`}
                        onClick={() => setAccountsExpanded((current) => !current)}
                    >
                        {accountsExpanded ? 'Less...' : '...'}
                    </div>
                </div>
            ) : (
                orderedAccounts?.map(mapFn)
            )}
        </>
    );
};

export const isFiatLink = (link: MergedAccountLink): link is FiatAccountLink => {
    return (link as FiatAccountLink).currencySymbol !== undefined;
};
export const isAccountLink = (link: MergedAccountLink): link is FiatAccountLink => {
    return link.elementType === 'Accounts';
};
export const isCardLink = (link: MergedAccountLink): link is CardAccountLink => {
    return (link as CardAccountLink).cardNumber !== undefined;
};
export const isCryptoLink = (link: MergedAccountLink): link is CryptoAccountLink => {
    return (link as CryptoAccountLink).code !== undefined;
};

const parseAsFiatAccountLink = (
    link: CryptoAccountLink,
    t: (key: string, options?: any) => any
): FiatAccountLink => {
    const { id, balance, availableBalance, code, symbol, childElements, addDate } = link;
    return {
        id,
        addDate,
        balance,
        availableBalance,
        childElements,
        currencySymbol: symbol,
        currencyCode: code,
        accountName: t('cryptoAccountName', { code }),
        accountNumber: '',
        routingNumber: '',
        currencyName: code,
    };
};
